import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import PageDetailModal from './PageDetailModal';
import CreatePageModal from './CreatePageModal';
import EditPageModal from './EditPageModal';
import PageAlertsModal from './PageAlertsModal';
import { Link } from 'react-router-dom';

const SitePages = ({ site }) => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, page: null });
  const [deleteError, setDeleteError] = useState(null);
  const [createModal, setCreateModal] = useState({ isOpen: false });
  const [editModal, setEditModal] = useState({ isOpen: false, page: null });
  const [alertsModal, setAlertsModal] = useState({ isOpen: false, page: null });
  const [checkingPages, setCheckingPages] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  // Удаляем pageAlerts и loadingAlerts

  useEffect(() => {
    if (site && site.id) {
      fetchPages();
    }
  }, [site]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${site.id}/pages`);
      const data = await response.json();

      if (response.ok && data.success) {
        const pagesData = data.pages || [];
        setPages(pagesData);
        // Больше не нужно получать алерты отдельно
        return pagesData;
      } else {
        setError(data.message || 'Ошибка загрузки страниц');
        return [];
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError('Ошибка сети при загрузке страниц');
      }
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Удаляем fetchAlertsForPages и handleRefreshAlerts

  const formatDate = (timestamp) => {
    if (!timestamp) return currentLang === 'ru' ? 'Не указана' : 'Not specified';
    return new Date(timestamp * 1000).toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US');
  };

  const decodeBase64 = (str) => {
    if (!str) return '';
    
    // Проверяем, является ли строка Base64
    const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(str) && str.length % 4 === 0;
    
    if (isBase64) {
      try {
        const decoded = atob(str);
        // Декодируем UTF-8 если данные пришли в неправильной кодировке
        try {
          return decodeURIComponent(escape(decoded));
        } catch {
          // Если не удалось декодировать как UTF-8, возвращаем как есть
          return decoded;
        }
      } catch {
        // Если не удалось декодировать Base64, возвращаем исходную строку
        return str;
      }
    } else {
      // Если это не Base64, возвращаем строку как есть
      return str;
    }
  };

  const parseHeaders = (textH) => {
    if (!textH) return {};
    
    try {
      // Пытаемся распарсить PHP serialize данные
      const decoded = decodeBase64(textH);
      const headers = {};
      
      // Более точное извлечение заголовков из PHP serialize формата
      const h1Regex = /h1.*?text.*?{([^}]*(?:{[^}]*}[^}]*)*)}/g;
      const h2Regex = /h2.*?text.*?{([^}]*(?:{[^}]*}[^}]*)*)}/g;
      const h3Regex = /h3.*?text.*?{([^}]*(?:{[^}]*}[^}]*)*)}/g;
      
      // Извлекаем H1
      const h1Matches = [...decoded.matchAll(h1Regex)];
      if (h1Matches.length > 0) {
        const h1Content = h1Matches[0][1];
        const h1Items = h1Content.match(/"([^"]+)"/g)?.map(match => match.replace(/"/g, '')) || [];
        // Дополнительная обработка кодировки для каждого элемента
        headers.h1 = h1Items.map(item => {
          try {
            return decodeURIComponent(escape(item));
          } catch {
            return item;
          }
        });
      }
      
      // Извлекаем H2
      const h2Matches = [...decoded.matchAll(h2Regex)];
      if (h2Matches.length > 0) {
        const h2Content = h2Matches[0][1];
        const h2Items = h2Content.match(/"([^"]+)"/g)?.map(match => match.replace(/"/g, '')) || [];
        headers.h2 = h2Items.map(item => {
          try {
            return decodeURIComponent(escape(item));
          } catch {
            return item;
          }
        });
      }
      
      // Извлекаем H3
      const h3Matches = [...decoded.matchAll(h3Regex)];
      if (h3Matches.length > 0) {
        const h3Content = h3Matches[0][1];
        const h3Items = h3Content.match(/"([^"]+)"/g)?.map(match => match.replace(/"/g, '')) || [];
        headers.h3 = h3Items.map(item => {
          try {
            return decodeURIComponent(escape(item));
          } catch {
            return item;
          }
        });
      }
      
      return headers;
    } catch (error) {
      return {};
    }
  };

  const parseKeywords = (keyData) => {
    if (!keyData) return [];
    
    try {
      const decoded = decodeBase64(keyData);
      // Более точное извлечение ключевых слов из PHP serialize формата
      const keywords = [];
      
      // Ищем все строки в кавычках
      const matches = decoded.match(/"([^"]+)"/g);
      if (matches) {
        const extractedKeywords = matches.map(match => match.replace(/"/g, ''));
        // Дополнительная обработка кодировки для каждого ключевого слова
        keywords.push(...extractedKeywords.map(keyword => {
          try {
            return decodeURIComponent(escape(keyword));
          } catch {
            return keyword;
          }
        }));
      }
      
      // Если не нашли в кавычках, пробуем другой формат
      if (keywords.length === 0) {
        const altMatches = decoded.match(/s:\d+:"([^"]+)"/g);
        if (altMatches) {
          const altKeywords = altMatches.map(match => {
            const content = match.match(/s:\d+:"([^"]+)"/);
            return content ? content[1] : '';
          }).filter(Boolean);
          
          keywords.push(...altKeywords.map(keyword => {
            try {
              return decodeURIComponent(escape(keyword));
            } catch {
              return keyword;
            }
          }));
        }
      }
      
      return keywords;
    } catch (error) {
      return [];
    }
  };

  const parseTextTd = (textTd) => {
    if (!textTd) return null;
    
    try {
      const decoded = decodeBase64(textTd);
      
      // Парсим PHP serialize формат
      const result = {};
      
      // Извлекаем size
      const sizeMatch = decoded.match(/size";d:([^;]+)/);
      if (sizeMatch) {
        result.size = parseFloat(sizeMatch[1]);
      }
      
      // Извлекаем down_time
      const downTimeMatch = decoded.match(/down_time";d:([^;]+)/);
      if (downTimeMatch) {
        result.down_time = parseFloat(downTimeMatch[1]);
      }
      
      // Извлекаем speed
      const speedMatch = decoded.match(/speed";d:([^;]+)/);
      if (speedMatch) {
        result.speed = parseFloat(speedMatch[1]);
      }
      
      // Извлекаем text (строка, а не массив)
      const textMatch = decoded.match(/text";s:\d+:"([^"]+)"/);
      if (textMatch) {
        try {
          const decodedText = decodeURIComponent(escape(textMatch[1]));
          result.text = decodedText;
        } catch {
          result.text = textMatch[1];
        }
      }
      
      // Извлекаем headers
      const headersMatch = decoded.match(/headers";a:\d+:({[^}]*})/);
      if (headersMatch) {
        const headersContent = headersMatch[1];
        const headers = {};
        
        // Извлекаем server
        const serverMatch = headersContent.match(/server";s:\d+:"([^"]+)"/);
        if (serverMatch) {
          headers.server = serverMatch[1];
        }
        
        // Извлекаем date
        const dateMatch = headersContent.match(/date";s:\d+:"([^"]+)"/);
        if (dateMatch) {
          headers.date = dateMatch[1];
        }
        
        // Извлекаем content-type
        const contentTypeMatch = headersContent.match(/content-type";s:\d+:"([^"]+)"/);
        if (contentTypeMatch) {
          headers['content-type'] = contentTypeMatch[1];
        }
        
        // Извлекаем x-powered-by
        const poweredByMatch = headersContent.match(/x-powered-by";s:\d+:"([^"]+)"/);
        if (poweredByMatch) {
          headers['x-powered-by'] = poweredByMatch[1];
        }
        
        result.headers = headers;
      }
      
      return result;
    } catch (error) {
      return null;
    }
  };

  const handleOpenModal = (page) => {
    setSelectedPage(page);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPage(null);
  };

  const handlePageUpdated = async () => {
    // Обновляем список страниц и алерты
    await fetchPages();
  };

  const handleReopenModal = async () => {
    // Закрываем модалку
    setIsModalOpen(false);
    
    // Обновляем данные и получаем обновленный список
    await fetchPages();
    
    // Переоткрываем модалку с обновленными данными
    setTimeout(() => {
      if (selectedPage) {
        setIsModalOpen(true);
      }
    }, 100);
  };

  const handleDeletePage = (page) => {
    setDeleteConfirmModal({ isOpen: true, page });
  };

  const handleCloseDeleteModal = () => {
    setDeleteConfirmModal({ isOpen: false, page: null });
  };

  const handleEditPage = (page) => {
    setEditModal({ isOpen: true, page });
  };

  const handleCloseEditModal = () => {
    setEditModal({ isOpen: false, page: null });
  };

  const handleOpenAlertsModal = (page) => {
    setAlertsModal({ isOpen: true, page });
  };

  const handleCloseAlertsModal = () => {
    setAlertsModal({ isOpen: false, page: null });
  };

  const handlePageCreated = async () => {
    await fetchPages();
  };

  // Компонент для отображения алертов
  const AlertsBlock = ({ page }) => {
    const alerts = page.alerts?.data;
    // loadingAlerts больше не нужен
    if (!alerts || !alerts.total_counts) {
      return null;
    }
    const { total_counts } = alerts;
    const hasAlerts = total_counts.critical > 0 || total_counts.warning > 0 || total_counts.info > 0;
    if (!hasAlerts) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs sm:text-sm font-medium text-green-800">
                {currentLang === 'ru' ? 'Проблем не обнаружено' : 'No issues found'}
              </span>
            </div>
            <button
              onClick={() => handleOpenAlertsModal(page)}
              className="px-2 py-1 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition-colors self-start sm:self-auto"
              title={currentLang === 'ru' ? 'Подробные алерты' : 'Detailed alerts'}
            >
              {currentLang === 'ru' ? 'Подробнее' : 'Details'}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
        <div className="flex flex-wrap gap-1 sm:gap-2">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-xs sm:text-sm font-medium text-red-800">
              {currentLang === 'ru' ? 'Обнаружены проблемы' : 'Issues detected'}
            </span>
            <span className="text-xs text-red-600 font-medium">
              {total_counts.critical + total_counts.warning + total_counts.info} {currentLang === 'ru' ? 'всего' : 'total'}
            </span>
          </div>
          {total_counts.critical > 0 && (
            <div className="flex items-center space-x-1 bg-red-100 px-2 py-1 rounded-full">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-600 rounded-full flex-shrink-0"></div>
              <span className="text-xs font-medium text-red-800">
                {total_counts.critical} <span className="hidden sm:inline">{currentLang === 'ru' ? 'критич.' : 'critical'}</span>
                <span className="sm:hidden">{currentLang === 'ru' ? 'крит.' : 'crit'}</span>
              </span>
            </div>
          )}
          {total_counts.warning > 0 && (
            <div className="flex items-center space-x-1 bg-yellow-100 px-2 py-1 rounded-full">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-600 rounded-full flex-shrink-0"></div>
              <span className="text-xs font-medium text-yellow-800">
                {total_counts.warning} <span className="hidden sm:inline">{currentLang === 'ru' ? 'предупрежд.' : 'warning'}</span>
                <span className="sm:hidden">{currentLang === 'ru' ? 'пред.' : 'warn'}</span>
              </span>
            </div>
          )}
          {total_counts.info > 0 && (
            <div className="flex items-center space-x-1 bg-blue-100 px-2 py-1 rounded-full">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
              <span className="text-xs font-medium text-blue-800">
                {total_counts.info} <span className="hidden sm:inline">{currentLang === 'ru' ? 'инфо' : 'info'}</span>
                <span className="sm:hidden">{currentLang === 'ru' ? 'инф.' : 'inf'}</span>
              </span>
            </div>
          )}
          <button
            onClick={() => handleOpenAlertsModal(page)}
            className="px-2 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors ml-auto"
            title={currentLang === 'ru' ? 'Подробные алерты' : 'Detailed alerts'}
          >
            {currentLang === 'ru' ? 'Подробнее' : 'Details'}
          </button>
        </div>
      </div>
    );
  };

  const handleCheckPage = async (page) => {
    try {
      // Добавляем страницу в список проверяемых
      setCheckingPages(prev => new Set(prev).add(page.id));

      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${site.id}/pages/${page.id}/check`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем список страниц и алерты
        await fetchPages();
      } else {
        // Можно добавить уведомление об ошибке
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
      } else {
      }
      // Можно добавить уведомление об ошибке
    } finally {
      // Удаляем страницу из списка проверяемых
      setCheckingPages(prev => {
        const newSet = new Set(prev);
        newSet.delete(page.id);
        return newSet;
      });
    }
  };

  // Удаляем handleRefreshAlerts

  const handleConfirmDelete = async () => {
    if (!deleteConfirmModal.page) return;

    try {
      setLoading(true);
      setDeleteError(null);

      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${site.id}/pages/${deleteConfirmModal.page.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем список страниц
        await fetchPages();
        handleCloseDeleteModal();
      } else {
        setDeleteError(data.message || 'Ошибка удаления');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setDeleteError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setDeleteError(error.message || 'Ошибка сети при удалении');
      }
      
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация страниц по поисковому запросу
  const filteredPages = pages.filter(page => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const url = page.url?.toLowerCase() || '';
    const title = decodeBase64(page.title)?.toLowerCase() || '';
    
    return url.includes(query) || title.includes(query);
  });



  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
        {error}
      </div>
    );
  }

  if (!pages || pages.length === 0) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">
            {currentLang === 'ru' ? 'Страницы не найдены' : 'No pages found'}
          </h2>
          {user?.role === 'admin' && (
          <div className="flex items-center">
            <button
              onClick={() => setCreateModal({ isOpen: true })}
              className="px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{currentLang === 'ru' ? 'Добавить страницу' : 'Add Page'}</span>
            </button>
          </div>
          )}
        </div>

        {/* Модальное окно создания страницы */}
        <CreatePageModal
          siteId={site?.id}
          isOpen={createModal.isOpen}
          onClose={() => setCreateModal({ isOpen: false })}
          onPageCreated={handlePageCreated}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
        <h2 className="text-lg sm:text-xl font-bold text-gray-800">
          {currentLang === 'ru' ? 'Важные страницы сайта' : 'Site Pages'}
        </h2>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
          <span className="text-xs sm:text-sm text-gray-600">
            {currentLang === 'ru' ? 'Всего страниц:' : 'Total pages:'} {pages.length}
            {searchQuery.trim() && (
              <span className="ml-1">
                ({currentLang === 'ru' ? 'найдено:' : 'found:'} {filteredPages.length})
              </span>
            )}
          </span>
          {user?.role === 'admin' && (
          <button
            onClick={() => setCreateModal({ isOpen: true })}
            className="px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{currentLang === 'ru' ? 'Добавить страницу' : 'Add Page'}</span>
          </button>
          )}
        </div>
      </div>

      {/* Поле поиска */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={currentLang === 'ru' ? 'Поиск по URL или заголовку...' : 'Search by URL or title...'}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="grid gap-4 sm:gap-6">
        {filteredPages.length === 0 && searchQuery.trim() ? (
          <div className="text-center py-8">
            <div className="text-gray-500 text-sm">
              {currentLang === 'ru' 
                ? `По запросу "${searchQuery}" ничего не найдено` 
                : `No results found for "${searchQuery}"`
              }
            </div>
          </div>
        ) : (
          filteredPages.map((page) => {
            const headers = parseHeaders(page.text_h);
            const keywords = parseKeywords(page.key);
            return (
            <div key={page.id} className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="space-y-4">
                
                {/* URL и статус */}
                <div className="flex flex-col sm:flex-row justify-between items-start border-b border-gray-200 pb-4 gap-4">
                  <div className="flex flex-row items-center sm:items-center gap-2 sm:gap-1 flex-1 min-w-0 w-full">
                    <div className="flex flex-row items-center space-between">
                      <button
                       onClick={() => handleOpenModal(page)}
                       className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                       title={currentLang === 'ru' ? 'Просмотр страницы' : 'View page'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      
                    </div>
                    <div className="max-w-[250px] md:max-w-[400px] lg:max-w-[600px] overflow-hidden">
                      <Link to={'#'} onClick={() => handleOpenModal(page)}>
                        <span className="truncate">{page.url}</span>
                      </Link>
                    </div>
                    <a 
                      href={page.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm sm:text-lg font-semibold text-blue-600 hover:text-blue-800 break-all flex items-center gap-2 ml-auto md:ml-0"
                      title={currentLang === 'ru' ? 'Открыть в новой вкладке' : 'Open in new tab'}
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => handleOpenModal(page)}
                      className="px-2 sm:px-3 py-1 bg-brand-500 text-white text-xs rounded-lg hover:bg-brand-600 transition-colors"
                    >
                      {currentLang === 'ru' ? 'Подробнее' : 'Details'}
                    </button>
                    {user?.role === 'admin' && (
                    <button
                      onClick={() => handleEditPage(page)}
                      className="px-2 sm:px-3 py-1 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 transition-colors"
                      title={currentLang === 'ru' ? 'Редактировать страницу' : 'Edit page'}
                    >
                      {currentLang === 'ru' ? 'Изменить' : 'Edit'}
                    </button>
                    )}
                    <button
                        onClick={() => handleCheckPage(page)}
                        className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                        title={currentLang === 'ru' ? 'Обновить информацию' : 'Update page info'}
                        disabled={checkingPages.has(page.id)}
                      >
                        {checkingPages.has(page.id) ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                      </button>
                      
                    <span className="text-xs text-gray-500">
                      {formatDate(page.check_date)}
                    </span>
                    <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                      page.h_status?.includes('200') 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {page.h_status || 'N/A'}
                    </span>
                    {user?.role === 'admin' && (
                    <button
                      onClick={() => handleDeletePage(page)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                      title={currentLang === 'ru' ? 'Удалить страницу' : 'Delete page'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    )}
                  </div>
                </div>

                {/* Блок с алертами */}
                <AlertsBlock page={page} />
                
                {/* Комментарий к странице */}
                {page.text && (
                  <div className="bg-gray-50 p-3 sm:p-4 border-l-4 border-red-500 italic text-xs sm:text-sm text-red-500">
                    {page.text}
                  </div>
                )}
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    {/* Title */}
                    <div className='mb-2'>
                      <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-0">
                        {currentLang === 'ru' ? 'Title:' : 'Title:'}
                      </h3>
                      <p className="text-gray-800 text-xs sm:text-sm break-words">{decodeBase64(page.title)}</p>
                    </div>

                    <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
                      {currentLang === 'ru' ? 'Description:' : 'Description:'}
                    </h3>
                    <p className="text-gray-800 text-xs sm:text-sm break-words mb-3">{decodeBase64(page.description)}</p>

                    {/* Keywords */}
                    <div className='mb-2'>
                      <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-0">
                        {currentLang === 'ru' ? 'Keywords:' : 'Keywords:'}
                      </h3>
                      <p className="text-gray-800 text-xs sm:text-sm break-words">{decodeBase64(page.keywords)}</p>
                    </div>

                    {/* Canonical */}
                    <div className='mb-2'>
                      <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-0">
                        {currentLang === 'ru' ? 'Canonical:' : 'Canonical:'}
                      </h3>
                      <span className={`text-xs sm:text-sm px-2 py-1 rounded-full break-words ${decodeBase64(page.canonical) !== page.url ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {decodeBase64(page.canonical)}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    {/* Ключевые запросы */}
                    {keywords.length > 0 && (
                      <div className='mb-2'>
                        <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
                          {currentLang === 'ru' ? 'Ключевые запросы:' : 'Key Queries:'} ({keywords.length})
                        </h3>
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {keywords.map((keyword, index) => (
                            <span 
                              key={index} 
                              className="px-1 sm:px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Заголовки H1, H2, H3 */}
                <div className="flex flex-col gap-4">
                  {headers.h1 && headers.h1.length > 0 && (
                    <div className='mb-2'>
                      <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
                        {currentLang === 'ru' ? 'H1 заголовки:' : 'H1 Headers:'} ({headers.h1.length})
                      </h3>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {headers.h1.map((header, index) => (
                          <span 
                            key={index} 
                            className={`px-1 sm:px-2 py-1 text-xs rounded-full ${headers.h1.length === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                          >
                            {header}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {headers.h2 && headers.h2.length > 0 && (
                    <div className='mb-2'>
                      <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
                        {currentLang === 'ru' ? 'H2 заголовки:' : 'H2 Headers:'} ({headers.h2.length})
                      </h3>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {headers.h2.map((header, index) => (
                          <span 
                            key={index} 
                            className="px-1 sm:px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"
                          >
                            {header}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {headers.h3 && headers.h3.length > 0 && (
                    <div className='mb-2'>
                      <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
                        {currentLang === 'ru' ? 'H3 заголовки:' : 'H3 Headers:'} ({headers.h3.length})
                      </h3>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {headers.h3.map((header, index) => (
                          <span 
                            key={index} 
                            className="px-1 sm:px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
                          >
                            {header}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Статистика */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-sm sm:text-lg font-bold text-gray-800">{page.weight}</div>
                    <div className="text-xs text-gray-600">
                      {currentLang === 'ru' ? 'Сортировка (вес)' : 'Sorting (weight)'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm sm:text-lg font-bold text-gray-800">{page.linkIn}</div>
                    <div className="text-xs text-gray-600">
                      {currentLang === 'ru' ? 'Исходящие внутренние ссылки' : 'Outgoing internal links'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm sm:text-lg font-bold text-gray-800">{page.linkOut}</div>
                    <div className="text-xs text-gray-600">
                      {currentLang === 'ru' ? 'Исходящие внешние ссылки' : 'Outgoing external links'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm sm:text-lg font-bold text-gray-800">
                      {(() => {
                        const textTdData = parseTextTd(page.text_td);
                        return textTdData && textTdData.size ? `${(textTdData.size / 1024).toFixed(1)} KB` : 'N/A';
                      })()}
                    </div>
                    <div className="text-xs text-gray-600">
                      {currentLang === 'ru' ? 'Размер' : 'Size'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm sm:text-lg font-bold text-gray-800">
                      {(() => {
                        const textTdData = parseTextTd(page.text_td);
                        return textTdData && textTdData.down_time ? `${textTdData.down_time.toFixed(0)}ms` : 'N/A';
                      })()}
                    </div>
                    <div className="text-xs text-gray-600">
                      {currentLang === 'ru' ? 'Время загрузки' : 'Load Time'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm sm:text-lg font-bold text-gray-800">
                      {(() => {
                        const textTdData = parseTextTd(page.text_td);
                        return textTdData && textTdData.speed ? `${textTdData.speed.toFixed(1)}ms` : 'N/A';
                      })()}
                    </div>
                    <div className="text-xs text-gray-600">
                      {currentLang === 'ru' ? 'Скорость' : 'Speed'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
        )}
      </div>

      {/* Модальное окно с деталями страницы */}
      <PageDetailModal
        pageId={selectedPage?.id}
        siteId={site?.id}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onPageUpdated={handlePageUpdated}
        onReopenModal={handleReopenModal}
      />

      {/* Модальное окно подтверждения удаления */}
      {deleteConfirmModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-bold text-red-600">
                  {currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
                </h3>
                <button
                  onClick={handleCloseDeleteModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {deleteError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                  {deleteError}
                </div>
              )}
              
              <div className="mb-6">
                <p className="text-gray-700 mb-2 text-sm">
                  {currentLang === 'ru' 
                    ? 'Вы уверены, что хотите удалить страницу?' 
                    : 'Are you sure you want to delete this page?'
                  }
                </p>
                <p className="text-xs sm:text-sm text-gray-600 break-all">
                  {deleteConfirmModal.page?.url}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={handleCloseDeleteModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                  disabled={loading}
                >
                  {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{currentLang === 'ru' ? 'Удаление...' : 'Deleting...'}</span>
                    </div>
                  ) : (
                    currentLang === 'ru' ? 'Удалить' : 'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно создания страницы */}
      <CreatePageModal
        siteId={site?.id}
        site={site}
        isOpen={createModal.isOpen}
        onClose={() => setCreateModal({ isOpen: false })}
        onPageCreated={handlePageCreated}
      />

      {/* Модальное окно редактирования страницы */}
      <EditPageModal
        page={editModal.page}
        siteId={site?.id}
        site={site}
        isOpen={editModal.isOpen}
        onClose={handleCloseEditModal}
        onPageUpdated={handlePageUpdated}
      />

      {/* Модальное окно с алертами страницы */}
      <PageAlertsModal
        pageId={alertsModal.page?.id}
        pageUrl={alertsModal.page?.url}
        isOpen={alertsModal.isOpen}
        onClose={handleCloseAlertsModal}
      />
    </div>
  );
};

export default SitePages; 